import {
  array,
  InferInput,
  InferOutput,
  literal,
  number,
  object,
  optional,
  pipe,
  safeParse,
  string,
  transform,
  union,
  ValiError,
} from 'valibot';
import {
  AlbumItemSchema,
  ArtistItemSchema,
  PlaylistItemSchema,
  SongItemSchema,
} from '../apiSchema.ts';
import { getOffsetNumber, taken } from '../utils.ts';

const DEFAULT_MAX_SEARCH_ITEMS = Number(
  import.meta.env.VITE_DEFAULT_MAX_SEARCH_ITEMS ?? '5',
);
const DEFAULT_MAX_SEARCH_RESULTS = 3;

export const CatalogSearchRequestArgsSchema = pipe(
  object({
    term: string(),
    limit: optional(number()),
    offset: optional(number()),
    type: optional(
      union([
        literal('albums'),
        literal('artists'),
        literal('playlists'),
        literal('tracks'),
      ]),
    ),
  }),
  transform(({ limit, offset, term, type }) => {
    return {
      term,
      limit: limit === undefined ? DEFAULT_MAX_SEARCH_ITEMS : limit,
      offset: offset === undefined ? 0 : offset,
      types: ((type) => {
        if (!type) {
          return [
            'albums',
            'artists',
            'playlists',
            'songs',
          ];
        }
        if (type === 'tracks') {
          return ['songs'];
        }
        return [type];
      })(type),
      ...(type ? null : { with: 'topResults' as const }),
    };
  }),
);

export type SearchRequestArgs = InferInput<
  typeof CatalogSearchRequestArgsSchema
>;

export type CatalogSearchRequestArgs = InferOutput<
  typeof CatalogSearchRequestArgsSchema
>;

export const CatalogSearchAPISchema = pipe(
  object({
    data: object({
      results: object({
        topResults: optional(object({
          data: array(
            union([
              ArtistItemSchema,
              AlbumItemSchema,
              PlaylistItemSchema,
              SongItemSchema,
            ]),
          ),
        })),
        artists: optional(object({
          data: array(ArtistItemSchema),
          next: optional(string()),
        })),
        albums: optional(object({
          data: array(AlbumItemSchema),
          next: optional(string()),
        })),
        songs: optional(object({
          data: array(SongItemSchema),
          next: optional(string()),
        })),
        playlists: optional(object({
          data: array(PlaylistItemSchema),
          next: optional(string()),
        })),
      }),
      meta: object({
        results: object({
          order: array(string()),
          rawOrder: array(string()),
        }),
      }),
    }),
  }),
  transform(({ data }) => {
    const {
      results,
      meta,
    } = data;
    const { albums, artists, playlists, songs, topResults } = results;
    const isCatalogXxxPage = Object.keys(results).length === 1;

    return {
      albumResults: albums?.data && {
        total: albums.data.length,
        next: albums.next === undefined
          ? undefined
          : getOffsetNumber(albums.next),
        albums: isCatalogXxxPage
          ? albums.data
          : taken(albums.data, DEFAULT_MAX_SEARCH_RESULTS),
      },
      artistResults: artists?.data && {
        total: artists.data.length,
        next: artists.next === undefined
          ? undefined
          : getOffsetNumber(artists.next),
        artists: isCatalogXxxPage
          ? artists.data
          : taken(artists.data, DEFAULT_MAX_SEARCH_RESULTS),
      },
      playlistResults: playlists?.data && {
        total: playlists.data.length,
        next: playlists.next === undefined
          ? undefined
          : getOffsetNumber(playlists.next),
        playlists: isCatalogXxxPage
          ? playlists.data
          : taken(playlists.data, DEFAULT_MAX_SEARCH_RESULTS),
      },
      trackResults: songs?.data && {
        total: songs.data.length,
        next: songs.next === undefined
          ? undefined
          : getOffsetNumber(songs.next),
        tracks: isCatalogXxxPage
          ? songs.data
          : taken(songs.data, DEFAULT_MAX_SEARCH_RESULTS),
      },
      topResults: topResults?.data && {
        total: topResults.data.length,
        resources: topResults.data,
      },
      order: meta.results.order.map((o) => o === 'songs' ? 'tracks' : o),
    };
  }),
);

export type SearchResponse = InferOutput<
  typeof CatalogSearchAPISchema
>;

export const $getCatalogSearch = async (
  player: MusicKit.MusicKitInstance,
  args: SearchRequestArgs,
): Promise<SearchResponse> => {
  const p = safeParse(CatalogSearchRequestArgsSchema, args);
  if (!p.success) {
    throw new ValiError(p.issues);
  }

  const response = await player.api.music(
    `/v1/catalog/${player.storefrontId}/search`,
    p.output,
  );
  const parsed = safeParse(CatalogSearchAPISchema, response);
  if (!parsed.success) {
    console.error(JSON.stringify(parsed.issues, null, 2));
    throw new ValiError(parsed.issues);
  }

  return parsed.output;
};
